import React, { Component } from 'react';

import { AxiosResponse } from 'axios';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';

import { LoadingButton } from '@atlaskit/button';
import RetryIcon from '@atlaskit/icon/glyph/retry';
import Tooltip from '@atlaskit/tooltip';

import {
  TestCaseBoxNewStep,
  TestCaseBoxStep,
  TestStepsHeader,
} from '@qality/common-components';
import { messages } from '@qality/common-messages';
import {
  TestStep,
  TestStepsCollection,
  TestStepsView,
} from '@qality/common-model';
import {
  DragDropService,
  StorageOfUniqueServices,
  TestStepService,
} from '@qality/common-service';
import debounce from '@qality/common-service/src/DebounceFunction';
import { QAlityIssueViewContext } from '@qality/common-state';

import { BorderStyled, ContainerStyled } from './TestSteps.styled';
import {
  deleteTestStepAttachments,
  getAttachmentsIdsList,
} from './TestSteps.util';

type Props = {
  canEdit: boolean;
  testStepsCollection?: TestStepsCollection;
  view: TestStepsView;
  expanded: boolean;
  onUpdateTestSteps: (testSteps: TestStepsCollection) => Promise<void>;
  testCaseIssueKey: string;
  testCaseIssueId?: number;
  droppableId: string;
  isFullscreenMode?: boolean;
};

type State = {
  saveStepsFailed: boolean;
  retryLoading: boolean;
};

const debouncedUpdatePositions = debounce(
  // TODO: no idea what the problem here is and in 2 places below where it's used
  (testCaseId, testSteps) =>
    TestStepService.testStepUpdatePositions(testCaseId, testSteps),
  1000
);

export default class TestSteps extends Component<Props, State> {
  static defaultProps = {
    isFullscreenMode: false,
  };

  static contextType = QAlityIssueViewContext;

  constructor(props: Props) {
    super(props);
    this.state = {
      saveStepsFailed: false,
      retryLoading: false,
    };
  }

  mapTestStepsToComponents = (): Array<JSX.Element> => {
    return this.props.testStepsCollection?.testSteps
      ?.filter((testStep) => {
        return !testStep.isNew;
      })
      .map((testStep, testStepIndex) => {
        return (
          <Draggable
            draggableId={String(testStep.id)}
            isDragDisabled={!this.props.canEdit}
            index={testStepIndex}
            key={testStep.id}
          >
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.draggableProps}>
                <TestCaseBoxStep
                  dragHandleProps={provided.dragHandleProps}
                  canEdit={this.props.canEdit}
                  testStep={testStep}
                  view={this.props.view}
                  testStepIndex={testStepIndex}
                  onUpdateTestStep={this.updateTestSteps}
                  onDeleteTestStep={this.deleteTestStepFromCollection}
                  testCaseIssueKey={this.props.testCaseIssueKey}
                  cloneTestStep={this.cloneTestStep}
                  canDeleteAllAttachment={this.context.canDeleteAllAttachment}
                  canCreateAttachment={this.context.canCreateAttachment}
                  canDeleteOwnAttachment={this.context.canDeleteOwnAttachment}
                  currentUser={this.context.currentUser}
                  snapshot={snapshot}
                  stepSaveFailed={testStep.stepSaveFailed}
                  getShouldDeleteAttachment={this.getShouldDeleteAttachment}
                />
              </div>
            )}
          </Draggable>
        );
      });
  };

  testCaseBoxNewStep = (): Array<JSX.Element> => {
    return this.props.testStepsCollection?.testSteps?.map(
      (testStep, testStepIndex) => {
        return testStep.isNew ? (
          <TestCaseBoxNewStep
            canEdit={this.props.canEdit}
            key={testStep.id}
            testStep={testStep}
            view={this.props.view}
            testStepIndex={testStepIndex}
            onUpdateTestStep={this.updateTestSteps}
            deleteTestStep={this.deleteTestStepFromCollection}
            testCaseIssueId={this.props.testCaseIssueId}
            canCreateAttachment={this.context.canCreateAttachment}
            isFullscreenMode={this.props.isFullscreenMode}
          />
        ) : null;
      }
    );
  };

  checkNewStepSaveStatus = (
    isNewTestStep: boolean,
    testStep: TestStep
  ): void => {
    if (isNewTestStep && testStep.origin) {
      const testStepPromise = testStep.origin as Promise<AxiosResponse>;
      // eslint-disable-next-line promise/catch-or-return
      testStepPromise.then((response) => {
        // eslint-disable-next-line promise/always-return
        if (response?.status === 200) {
          return;
        }

        // Get newest step index and object since user could alter it in the meantime.
        const newTestStepIndex =
          this.props.testStepsCollection.testSteps.findIndex(
            (currentTestStep) => currentTestStep.id === testStep.id
          );

        // eslint-disable-next-line promise/always-return
        if (newTestStepIndex === -1) {
          return;
        }

        const newTestStep =
          this.props.testStepsCollection.testSteps[newTestStepIndex];

        newTestStep.stepSaveFailed = true;

        this.updateTestSteps(newTestStepIndex, newTestStep);

        this.setState({
          saveStepsFailed: true,
        });
      });
    }
  };

  updateTestSteps = async (
    testStepIndex: number,
    testStep: TestStep,
    isNewTestStep = false
  ): Promise<void> => {
    if (this.props.testStepsCollection) {
      const testSteps = [...this.props.testStepsCollection.testSteps];
      testSteps[testStepIndex] = testStep;
      if (isNewTestStep && this.props.testCaseIssueId) {
        const newTestStep = new TestStep();
        newTestStep.testCaseId = this.props.testCaseIssueId;
        testSteps.push(newTestStep);
      }
      this.props.onUpdateTestSteps(new TestStepsCollection(testSteps));
    }

    this.checkNewStepSaveStatus(isNewTestStep, testStep);
  };

  getFailedSteps = async (): Promise<TestStep[]> => {
    const testSteps: TestStep[] = [];
    await Promise.all(
      this.props.testStepsCollection.testSteps.map(async (testStep) => {
        if (testStep.origin) {
          const testStepResponse = await testStep.origin;
          if (testStepResponse?.status === 200) {
            return;
          }

          testSteps.push(testStep);
        }
      })
    );

    return testSteps;
  };

  retryStepsWithFailedOrigin = async (testSteps: TestStep[]): Promise<void> => {
    const promises = [];

    for (const testStep of testSteps) {
      testStep.origin = TestStepService.createTestStep(testStep);
      promises.push(testStep.origin);
      // eslint-disable-next-line promise/param-names
      await new Promise((r) => setTimeout(r, 500)); // delay steps to make sure they are saved in a correct order
    }

    await Promise.all(promises);
  };

  checkFailCount = async (): Promise<void> => {
    let failedCount = 0;

    await Promise.all(
      this.props.testStepsCollection.testSteps.map(async (testStep, index) => {
        if (testStep.origin) {
          const testStepResponse = await testStep.origin;
          if (testStepResponse?.status === 200) {
            testStep.stepSaveFailed = false;
            this.updateTestSteps(index, testStep);
          } else {
            failedCount += 1;
          }
        }
      })
    );

    if (failedCount === 0) {
      this.setState({ saveStepsFailed: false });
      debouncedUpdatePositions(
        this.props.testCaseIssueId,
        this.props.testStepsCollection.testSteps
      );
    }
  };

  retryFailedSteps = async (): Promise<void> => {
    this.setState({ retryLoading: true });

    const testSteps = await this.getFailedSteps();

    await this.retryStepsWithFailedOrigin(testSteps);

    await this.checkFailCount();

    this.setState({ retryLoading: false });
  };

  deleteTestStepFromCollection = (
    testStepIndex: number,
    testStep: TestStep
  ): void => {
    const { testStepsCollection } = this.props;

    deleteTestStepAttachments(testStep, testStepsCollection);

    testStepsCollection?.testSteps?.splice(testStepIndex, 1);
    this.props.onUpdateTestSteps(testStepsCollection);
  };

  getShouldDeleteAttachment = (fileId: number, testStep: TestStep): boolean => {
    const attachmentsIds = getAttachmentsIdsList(
      testStep?.id,
      this.props.testStepsCollection
    );
    return !attachmentsIds.includes(fileId);
  };

  onDragEnd = (result: DropResult): void => {
    DragDropService.handleDragEnd(
      this.props.testStepsCollection?.testSteps,
      result
    );
    TestStepService.testStepUpdatePositions(
      this.props.testCaseIssueId,
      this.props.testStepsCollection?.testSteps
    );
    const { testStepsCollection } = this.props;
    this.props.onUpdateTestSteps(testStepsCollection);
  };

  cloneTestStep = async (testStepIndex: number): Promise<void | boolean> => {
    try {
      const testStep = this.props.testStepsCollection?.testSteps[testStepIndex];

      let newTestStep = new TestStep(
        testStep?.testCaseId,
        testStep?.step,
        testStep?.result,
        testStep?.data,
        testStep?.attachments
      );
      // TODO:
      // this spread operator is causing bugs.
      // newTestStep is no longer a TestStep but a simple object.
      newTestStep = {
        ...newTestStep,
        isNew: false,
        origin: TestStepService.createTestStep(newTestStep),
      };
      this.checkNewStepSaveStatus(true, newTestStep);

      const newTestSteps = this.props.testStepsCollection?.testSteps;

      newTestSteps.splice(testStepIndex + 1, 0, newTestStep);
      const testStepsCollection = new TestStepsCollection(newTestSteps);
      debouncedUpdatePositions(
        this.props.testCaseIssueId,
        testStepsCollection.testSteps
      );
      this.props.onUpdateTestSteps(testStepsCollection);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.cloneTestStep.message']
      );
      return true;
    }
  };

  render(): JSX.Element {
    if (!this.props.expanded) {
      return null;
    }
    return (
      <ContainerStyled shouldEnableOverflowScroll={this.props.isFullscreenMode}>
        <TestStepsHeader view={this.props.view} />
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable
            droppableId={this.props.droppableId}
            isDropDisabled={!this.props.canEdit}
          >
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {this.mapTestStepsToComponents()}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {this.testCaseBoxNewStep()}
        <BorderStyled shouldAddMargin={this.props.isFullscreenMode} />
        {this.state.saveStepsFailed && (
          <div className='retry-all-button-wrapper'>
            <Tooltip
              content='This button will try to save all your steps again. If the problem persist please contact our support.'
              position='bottom'
            >
              <LoadingButton
                iconBefore={<RetryIcon label='retry icon' />}
                onClick={this.retryFailedSteps}
                isLoading={this.state.retryLoading}
              >
                Retry saving test steps
              </LoadingButton>
            </Tooltip>
          </div>
        )}
      </ContainerStyled>
    );
  }
}
